import React, { useState, useContext, useEffect } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { formatPhone } from '@/components/admin/util';
import ImageUploadField from '@/components/operations/ImageUploadField';

const TYPES = ['financial', 'education', 'commercial', 'residential', 'government', 'internal'];
const PAYMENT_TERMS = ['NET15', 'NET30', 'NET45', 'NET60', 'Due on Receipt'];

const Section = ({ title, children }) => (
  <div className="space-y-3">
    <h4 className="text-xs font-bold uppercase tracking-wider text-[#8FC93A]">{title}</h4>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">{children}</div>
  </div>
);

const F = ({ label, children, full }) => (
  <div className={`space-y-1 ${full ? 'md:col-span-2' : ''}`}>
    <Label className="text-xs text-slate-600">{label}</Label>
    {children}
  </div>
);

const EMPTY = {
  name: '', type: 'commercial', industry: '', status: 'active',
  account_number: '', account_code: '',
  primary_contact_name: '', primary_contact_email: '', primary_contact_phone: '',
  billing_contact_name: '', billing_contact_email: '', billing_contact_phone: '',
  main_contact_name: '', main_contact_title: '', main_contact_email: '', main_contact_phone: '', main_contact_notes: '',
  cc_email_list: '', website: '', tax_id: '', phone: '', email: '',
  billing_address: '', billing_city: '', billing_state: '', billing_zip: '',
  contract_start_date: '', contract_end_date: '', payment_terms: '',
  max_requestors_per_location: 2, notes: '', internal_notes: '',
  assigned_operations_manager_id: '',
};

const CustomerForm = ({ customer, onClose, onSaved }) => {
  const { token } = useContext(AuthContext);
  const isEdit = !!customer;
  const [f, setF] = useState(() => {
    if (!customer) return { ...EMPTY };
    return {
      ...EMPTY,
      ...Object.fromEntries(Object.entries(customer).map(([k, v]) => [k, v == null ? '' : v])),
      cc_email_list: (customer.cc_email_list || []).join(', '),
    };
  });
  const [busy, setBusy] = useState(false);
  const [opsManagers, setOpsManagers] = useState([]);
  const [awards, setAwards] = useState([]);
  useEffect(() => {
    if (!isEdit) return;
    axios.get(`${API}/customers/${customer.id}/awards`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setAwards(r.data || []))
      .catch(() => {});
  }, [isEdit, customer, token]);
  useEffect(() => {
    axios.get(`${API}/admin/users?user_type=Internal`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setOpsManagers((r.data || []).filter((u) => ['owner', 'admin', 'manager', 'tfs_operations'].includes(u.role))))
      .catch(() => {});
  }, [token]);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const blurPhone = (k) => () => setF((p) => ({ ...p, [k]: formatPhone(p[k]) }));

  const submit = async () => {
    if (!f.name.trim()) { toast.error('Customer name is required'); return; }
    if (!f.industry.trim()) { toast.error('Industry is required'); return; }
    setBusy(true);
    const payload = {
      ...f,
      cc_email_list: f.cc_email_list ? f.cc_email_list.split(',').map((s) => s.trim()).filter(Boolean) : [],
      max_requestors_per_location: parseInt(f.max_requestors_per_location, 10) || 2,
    };
    // Logo is managed exclusively by the dedicated upload/delete endpoint — never clobber it on Save.
    delete payload.logo_url;
    // additional_contacts is REACH-transferred and shown read-only; never clobber with a non-array.
    if (!Array.isArray(payload.additional_contacts)) delete payload.additional_contacts;
    try {
      if (isEdit) {
        await axios.put(`${API}/customers/${customer.id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Customer updated');
      } else {
        await axios.post(`${API}/customers`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Customer created');
      }
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save customer');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="customer-form-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#0E2A47]">{isEdit ? `Edit Customer — ${customer.name}` : 'Add Customer'}</DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-2">
          <Section title="Brand">
            <div className="md:col-span-2">
              <ImageUploadField
                resource="customers" field="logo" variant="logo"
                label="Customer Logo" testidPrefix="cust-logo"
                entityId={isEdit ? customer.id : null}
                hasImage={!!(customer && customer.logo_url)}
                hint="Shown across the client's ROUT portal"
              />
            </div>
          </Section>

          <Section title="Identity">
            <F label="Account #"><Input value={f.account_number} onChange={set('account_number')} data-testid="cust-account-number" /></F>
            <F label="Account Code (4-char)"><Input value={f.account_code} onChange={set('account_code')} maxLength={4} data-testid="cust-account-code" /></F>
            <F label="Customer Name *"><Input value={f.name} onChange={set('name')} data-testid="cust-name" /></F>
            <F label="Industry *"><Input value={f.industry} onChange={set('industry')} placeholder="banking, office, retail…" data-testid="cust-industry" /></F>
            <F label="Type">
              <select value={f.type} onChange={set('type')} data-testid="cust-type"
                className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm capitalize">
                {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </F>
            <F label="Max Requestors / Location">
              <Input type="number" min={1} max={50} value={f.max_requestors_per_location}
                onChange={set('max_requestors_per_location')} data-testid="cust-cap" />
            </F>
            <F label="Assigned Operations Manager" full>
              <select value={f.assigned_operations_manager_id || ''} onChange={set('assigned_operations_manager_id')} data-testid="cust-ops-manager"
                className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm">
                <option value="">— Unassigned —</option>
                {opsManagers.map((u) => <option key={u.id} value={u.id}>{u.full_name || u.name} ({u.role})</option>)}
              </select>
            </F>
          </Section>

          <Section title="Main Contact">
            <F label="Name"><Input value={f.main_contact_name} onChange={set('main_contact_name')} data-testid="cust-main-name" /></F>
            <F label="Title / Position"><Input value={f.main_contact_title} onChange={set('main_contact_title')} data-testid="cust-main-title" placeholder="e.g., Facilities Director" /></F>
            <F label="Email"><Input value={f.main_contact_email} onChange={set('main_contact_email')} data-testid="cust-main-email" /></F>
            <F label="Phone"><Input value={f.main_contact_phone} onChange={set('main_contact_phone')} onBlur={blurPhone('main_contact_phone')} data-testid="cust-main-phone" /></F>
            <F label="CC Email List (comma-separated)"><Input value={f.cc_email_list} onChange={set('cc_email_list')} data-testid="cust-cc" /></F>
            <F label="Contact Notes" full><Textarea rows={2} value={f.main_contact_notes} onChange={set('main_contact_notes')} /></F>
            {Array.isArray(f.additional_contacts) && f.additional_contacts.length > 0 && (
              <div className="md:col-span-2" data-testid="cust-additional-contacts">
                <div className="text-xs font-semibold text-slate-500">Other Transferred Contacts (REACH)</div>
                <div className="mt-1 space-y-1">
                  {f.additional_contacts.map((ct, i) => (
                    <div key={i} className="rounded border border-slate-200 bg-slate-50 px-2 py-1 text-xs text-slate-600">
                      {[ct.name, ct.title, ct.email, ct.phone].filter(Boolean).join(' · ')}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Section>

          <Section title="Primary & Billing Contacts">
            <F label="Primary Contact Name"><Input value={f.primary_contact_name} onChange={set('primary_contact_name')} /></F>
            <F label="Primary Contact Email"><Input value={f.primary_contact_email} onChange={set('primary_contact_email')} /></F>
            <F label="Primary Contact Phone"><Input value={f.primary_contact_phone} onChange={set('primary_contact_phone')} onBlur={blurPhone('primary_contact_phone')} /></F>
            <div />
            <F label="Billing Contact Name"><Input value={f.billing_contact_name} onChange={set('billing_contact_name')} /></F>
            <F label="Billing Contact Email"><Input value={f.billing_contact_email} onChange={set('billing_contact_email')} /></F>
            <F label="Billing Contact Phone"><Input value={f.billing_contact_phone} onChange={set('billing_contact_phone')} onBlur={blurPhone('billing_contact_phone')} /></F>
          </Section>

          <Section title="Billing Address & Business">
            <F label="Organization Phone"><Input value={f.phone} onChange={set('phone')} onBlur={blurPhone('phone')} data-testid="cust-org-phone" /></F>
            <F label="General Email"><Input value={f.email} onChange={set('email')} data-testid="cust-org-email" /></F>
            <F label="Billing Address" full><Input value={f.billing_address} onChange={set('billing_address')} /></F>
            <F label="City"><Input value={f.billing_city} onChange={set('billing_city')} /></F>
            <F label="State"><Input value={f.billing_state} onChange={set('billing_state')} /></F>
            <F label="ZIP"><Input value={f.billing_zip} onChange={set('billing_zip')} /></F>
            <F label="Website"><Input value={f.website} onChange={set('website')} /></F>
            <F label="Tax ID"><Input value={f.tax_id} onChange={set('tax_id')} /></F>
            <F label="Payment Terms">
              <select value={f.payment_terms || ''} onChange={set('payment_terms')}
                className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm">
                <option value="">—</option>
                {PAYMENT_TERMS.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </F>
            <F label="Contract Start"><Input type="date" value={(f.contract_start_date || '').slice(0, 10)} onChange={set('contract_start_date')} /></F>
            <F label="Contract End"><Input type="date" value={(f.contract_end_date || '').slice(0, 10)} onChange={set('contract_end_date')} /></F>
          </Section>

          <Section title="Notes">
            <F label="Notes" full><Textarea rows={2} value={f.notes} onChange={set('notes')} /></F>
            <F label="Internal Notes (TFS only)" full><Textarea rows={2} value={f.internal_notes} onChange={set('internal_notes')} /></F>
          </Section>

          {isEdit && (
            <div className="space-y-3" data-testid="customer-awards-section">
              <h4 className="text-xs font-bold uppercase tracking-wider text-[#8FC93A]">Awarded Contracts (REACH)</h4>
              {awards.length === 0 ? (
                <p className="text-sm text-slate-400" data-testid="customer-awards-empty">No awarded contracts handed off from REACH yet.</p>
              ) : (
                <div className="space-y-2">
                  {awards.map((a) => (
                    <div key={a.id} data-testid={`customer-award-${a.id}`} className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-mono text-xs font-bold text-[#0E2A47]">Opp {a.reach_opportunity_id}</span>
                        <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${a.status === 'active' ? 'bg-[#8FC93A]/20 text-[#4d7c0f]' : 'bg-slate-200 text-slate-600'}`}>{a.status}</span>
                        {a.contract_term && <span className="text-xs text-slate-500">{a.contract_term}</span>}
                      </div>
                      <div className="mt-1 grid grid-cols-2 gap-x-4 gap-y-0.5 text-xs text-slate-600">
                        {a.service_start_date && <div><span className="text-slate-400">Service start:</span> {String(a.service_start_date).slice(0, 10)}</div>}
                        {a.contract_end_date && <div><span className="text-slate-400">Contract end:</span> {String(a.contract_end_date).slice(0, 10)}</div>}
                        {a.payment_terms && <div><span className="text-slate-400">Payment terms:</span> {a.payment_terms}</div>}
                        {a.sales_owner_name && <div><span className="text-slate-400">Sales owner:</span> {a.sales_owner_name}</div>}
                        {a.pricing?.monthly_value != null && <div><span className="text-slate-400">Monthly:</span> ${Number(a.pricing.monthly_value).toLocaleString()}</div>}
                        {a.pricing?.first_12_month_value != null && <div><span className="text-slate-400">First 12 mo:</span> ${Number(a.pricing.first_12_month_value).toLocaleString()}</div>}
                        {a.pricing?.total_contract_value != null && <div><span className="text-slate-400">TCV:</span> ${Number(a.pricing.total_contract_value).toLocaleString()}</div>}
                        <div><span className="text-slate-400">Locations:</span> {(a.service_location_names || []).join(', ') || a.service_location_ids?.length || 0}</div>
                        {a.reach_organization_id && <div><span className="text-slate-400">REACH org:</span> {a.reach_organization_id}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <button onClick={onClose} className="rounded-md border border-gray-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-gray-50">Cancel</button>
          <button onClick={submit} disabled={busy} data-testid="cust-save"
            className="rounded-md bg-[#0E2A47] px-5 py-2 text-sm font-semibold text-white hover:bg-[#16324F] disabled:opacity-50">
            {busy ? 'Saving…' : (isEdit ? 'Save Changes' : 'Create Customer')}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CustomerForm;
